import { useState } from "react";
import {
  Clock,
  FileText,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  ImageIcon,
} from "lucide-react";
import { useCaseHistory, CaseHistory } from "../contexts/CaseHistoryContext";

interface SidebarProps {
  onNewCase: () => void;
  onSelectCase: (caseData: CaseHistory) => void;
  selectedCaseId?: string;
}

export function Sidebar({
  onNewCase,
  onSelectCase,
  selectedCaseId,
}: SidebarProps) {
  const { cases, loading, deleteCase, clearHistory } = useCaseHistory();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await deleteCase(id);
    } finally {
      setDeleting(null);
      setShowDeleteConfirm(null);
    }
  };

  const handleClearHistory = async () => {
    await clearHistory();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status: CaseHistory["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "processing":
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  // Deep search for license plate in any nested structure
  const findLicensePlate = (obj: any, depth = 0): string | null => {
    if (!obj || depth > 5) return null;

    // Check common field names
    const plateFields = [
      "licensePlate",
      "license_plate",
      "plateNumber",
      "plate_number",
      "plaka",
      "plate",
    ];
    for (const field of plateFields) {
      if (obj[field] && typeof obj[field] === "string") {
        return obj[field];
      }
    }

    // If it's an array, check first element
    if (Array.isArray(obj) && obj.length > 0) {
      const found = findLicensePlate(obj[0], depth + 1);
      if (found) return found;
    }

    // Search in nested objects
    if (typeof obj === "object") {
      for (const key of Object.keys(obj)) {
        if (typeof obj[key] === "object") {
          const found = findLicensePlate(obj[key], depth + 1);
          if (found) return found;
        }
      }
    }

    return null;
  };

  const getCaseSummary = (caseItem: CaseHistory) => {
    // Try to find license plate anywhere in results
    if (caseItem.results) {
      const plate = findLicensePlate(caseItem.results);
      if (plate) {
        return plate;
      }
    }

    // Try to parse vehicleInfo if it's JSON containing plate
    if (caseItem.summary.vehicleInfo) {
      const info = caseItem.summary.vehicleInfo;
      if (info.startsWith("[") || info.startsWith("{")) {
        try {
          const parsed = JSON.parse(info);
          const plate = findLicensePlate(parsed);
          if (plate) {
            return plate;
          }
        } catch {
          // Not valid JSON, continue
        }
      } else {
        // Not JSON, use as-is if it looks like a plate
        return info;
      }
    }

    if (caseItem.summary.location) {
      return caseItem.summary.location;
    }

    // Final fallback with file counts
    const parts = [];
    if (caseItem.summary.documentsCount > 0) {
      parts.push(`${caseItem.summary.documentsCount} belge`);
    }
    if (caseItem.summary.imagesCount > 0) {
      parts.push(`${caseItem.summary.imagesCount} görsel`);
    }
    return parts.length > 0 ? parts.join(", ") : "Analiz dosyası";
  };

  const onDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (showDeleteConfirm === id) {
      handleDelete(id);
    } else {
      setShowDeleteConfirm(id);
      setTimeout(() => setShowDeleteConfirm(null), 3000);
    }
  };

  if (isCollapsed) {
    return (
      <div className="w-16 bg-slate-900 flex flex-col items-center py-4 transition-all duration-300">
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors mb-4"
          title="Expand sidebar"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <button
          onClick={onNewCase}
          className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors mb-6"
          title="New Analysis"
        >
          <Plus className="w-5 h-5" />
        </button>

        <div className="flex-1 w-full px-2 space-y-2 overflow-y-auto">
          {cases.slice(0, 5).map((caseItem) => (
            <button
              key={caseItem.id}
              onClick={() => onSelectCase(caseItem)}
              className={`w-full p-2 rounded-lg transition-colors ${
                selectedCaseId === caseItem.id
                  ? "bg-slate-700"
                  : "hover:bg-slate-800"
              }`}
              title={getCaseSummary(caseItem)}
            >
              {getStatusIcon(caseItem.status)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 bg-slate-900 flex flex-col transition-all duration-300">
      {/* Header */}
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-slate-400" />
          <h2 className="font-semibold text-white">Case History</h2>
        </div>
        <button
          onClick={() => setIsCollapsed(true)}
          className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          title="Collapse sidebar"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* New Case Button */}
      <div className="p-4">
        <button
          onClick={onNewCase}
          className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Analysis
        </button>
      </div>

      {/* Cases List */}
      <div className="flex-1 overflow-y-auto px-3 pb-4">
        {loading ? (
          <div className="text-center py-8 px-4">
            <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-3 animate-spin" />
            <p className="text-slate-400 text-sm">Loading cases...</p>
          </div>
        ) : cases.length === 0 ? (
          <div className="text-center py-8 px-4">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No cases yet</p>
            <p className="text-slate-500 text-xs mt-1">
              Start a new analysis to see your history here
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {cases.map((caseItem) => (
              <div
                key={caseItem.id}
                onClick={() => onSelectCase(caseItem)}
                className={`group p-3 rounded-xl cursor-pointer transition-all ${
                  selectedCaseId === caseItem.id
                    ? "bg-slate-700/80 ring-1 ring-slate-600"
                    : "hover:bg-slate-800/60"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {getStatusIcon(caseItem.status)}
                    <span className="text-sm font-medium text-white truncate">
                      {getCaseSummary(caseItem)}
                    </span>
                  </div>
                  <button
                    onClick={(e) => onDeleteClick(e, caseItem.id)}
                    disabled={deleting === caseItem.id}
                    className={`p-1 rounded transition-colors flex-shrink-0 ${
                      deleting === caseItem.id
                        ? "text-slate-400 cursor-not-allowed"
                        : showDeleteConfirm === caseItem.id
                        ? "bg-red-500 text-white"
                        : "text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100"
                    }`}
                    title={
                      showDeleteConfirm === caseItem.id
                        ? "Click again to confirm"
                        : "Delete case"
                    }
                  >
                    {deleting === caseItem.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-400 mt-1">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(caseItem.createdAt)}
                  </span>

                  <div className="flex items-center gap-2">
                    {caseItem.summary.documentsCount > 0 && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {caseItem.summary.documentsCount}
                      </span>
                    )}

                    {caseItem.summary.imagesCount > 0 && (
                      <span className="flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        {caseItem.summary.imagesCount}
                      </span>
                    )}
                  </div>
                </div>

                {caseItem.status === "completed" &&
                  caseItem.summary.faultPercentage !== undefined && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full"
                          style={{
                            width: `${caseItem.summary.faultPercentage}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-slate-400">
                        {caseItem.summary.faultPercentage}%
                      </span>
                    </div>
                  )}

                {caseItem.status === "completed" &&
                  caseItem.summary.estimatedCost && (
                    <div className="mt-2 text-xs text-emerald-400">
                      Est. ₺{caseItem.summary.estimatedCost.toLocaleString()}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clear History */}
      {cases.length > 0 && (
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={clearHistory}
            className="w-full text-sm text-slate-500 hover:text-slate-400 transition-colors"
          >
            Clear all history
          </button>
        </div>
      )}
    </div>
  );
}
